import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Users, Award, Building2, Eye, ChevronRight, Quote, Star, Sparkles, MapPin } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { SectionHeader, FadeUp } from "../../components/public/Section";
import HeroSlider from "../../components/public/HeroSlider";
import HorizontalGallery from "../../components/public/HorizontalGallery";
import Counter from "../../components/public/Counter";

const ABOUT_IMG = "/uploads/images/about_gos-de3b3516.jpg";
const STATS = [
  { icon: Users, value: 450, suffix: "+", label: "Active Members" },
  { icon: Calendar, value: 40, suffix: "+", label: "Years of Legacy" },
  { icon: Award, value: 120, suffix: "+", label: "CME Programs" },
  { icon: Building2, value: 30, suffix: "+", label: "Partner Hospitals" },
];

const PARTNERS = [
  "Yashoda Hospital",
  "Max Healthcare",
  "Fortis Hospital",
  "Columbia Asia",
  "Le Crest Hospital",
  "Combined District Hospital",
];

export default function Home() {
  const s = useSettings();
  const [ann, setAnn] = useState([]);
  const [events, setEvents] = useState([]);
  const [committee, setCommittee] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/announcements?limit=4").then((r) => setAnn(r.data.items || []));
    api.get("/events?status=upcoming&limit=3").then((r) => setEvents(r.data.items || []));
    api.get("/organization").then((r) => {
      const office = (r.data?.sections || []).find((section) => section.name === "Office Bearers");
      setCommittee((office?.members || []).slice(0, 4));
    });
    api.get("/testimonials").then((r) => setTestimonials(r.data || []));
    api.get("/gallery/albums").then(async (r) => {
      const first = (r.data || [])[0];
      if (first) {
        const detail = await api.get(`/gallery/albums/${first.slug}`);
        setGallery(detail.data.items || []);
      }
    });
  }, []);

  return (
    <div>
      {/* HERO SLIDER */}
      <HeroSlider />

      {/* Floating stats */}
      <section className="relative -mt-20 md:-mt-24 z-10">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-2xl shadow-[0_30px_80px_-40px_rgba(0,86,166,0.35)] border border-white/60 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-brand-border/70"
          >
            {STATS.map((stat, i) => (
              <div key={i} className="p-7 md:p-8 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary-light to-white text-brand-primary flex items-center justify-center shrink-0 border border-brand-primary/10">
                  <stat.icon size={22} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-brand-text tracking-tight leading-none">
                    <Counter to={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-[13px] text-brand-muted mt-1.5 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_25px_80px_-25px_rgba(0,86,166,0.35)]">
                <img src={ABOUT_IMG} alt="About GOS" className="w-full h-[500px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 to-transparent" />
              </div>
              <div className="absolute -bottom-8 -right-4 md:-right-8 bg-white rounded-2xl px-6 py-5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)] border border-brand-border/60">
                <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted font-semibold">Since</div>
                <div className="font-display text-4xl font-extrabold text-brand-primary mt-1">1983</div>
              </div>
              <div className="absolute -top-6 -left-4 md:-left-8 hidden md:block bg-brand-primary text-white rounded-2xl px-5 py-4 shadow-lg">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]"><Sparkles size={12} className="text-brand-accent" /> ISO Recognized</div>
              </div>
            </div>
          </FadeUp>
          <div>
            <div className="eyebrow mb-4"><span className="eyebrow-line" />About the Society</div>
            <h2 className="font-display text-[clamp(2rem,3.6vw,3.25rem)] font-extrabold text-brand-text leading-[1.08] tracking-tight text-balance">
              A trusted voice for <span className="text-brand-primary">ophthalmology</span> in Ghaziabad since 1983
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed text-[17px]">
              GOS is a professional medical association bringing together ophthalmologists, surgeons, residents and fellows from Ghaziabad and the wider NCR. Our mission is to promote excellence in clinical practice through continuing medical education, scientific conferences, research collaboration and community eye health awareness.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                ["Continuing Medical Education", "Regular CME programs, workshops and hands-on training."],
                ["Scientific Conferences", "Annual conferences and specialty symposia."],
                ["Community Outreach", "Free screening camps and public awareness drives."],
                ["Academic Support", "Fellowships, mentorship and research funding."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-primary-light to-white text-brand-primary flex items-center justify-center shrink-0 border border-brand-primary/10">
                    <Eye size={16} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-brand-text text-[15px]">{t}</div>
                    <div className="text-[13px] text-brand-muted mt-1 leading-relaxed">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/about" className="btn-primary" data-testid="about-cta">Discover Our Story <ArrowRight size={18} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* LEADER MESSAGE */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-brand-primary-light/30 border-y border-brand-border/70">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #0056A6 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="container-wide py-20 md:py-28 grid grid-cols-1 lg:grid-cols-5 gap-14 items-center relative">
          <FadeUp className="lg:col-span-2">
            <div className="relative max-w-md">
              <div className="absolute inset-0 -translate-x-4 translate-y-4 rounded-3xl bg-gradient-to-br from-brand-primary to-brand-secondary opacity-15" />
              <img src={mediaUrl(s.leader_photo)} alt={s.leader_name} className="relative rounded-3xl w-full h-[520px] object-cover shadow-[0_25px_60px_-20px_rgba(0,86,166,0.35)]" />
              <div className="absolute -bottom-5 left-6 right-6 md:left-8 md:right-auto bg-white px-6 py-4 rounded-2xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] border border-brand-border/60">
                <div className="font-display font-bold text-brand-text">{s.leader_name || "Dr. Harish Gupta"}</div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-brand-primary font-semibold mt-1">GOS {s.leader_designation || "Chief Patron"}</div>
              </div>
            </div>
          </FadeUp>
          <div className="lg:col-span-3">
            <div className="eyebrow mb-4">
              <span className="eyebrow-line" />
              {s.leader_designation || "Chief Patron"}'s Message
            </div>

            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] font-extrabold text-brand-text leading-[1.1] tracking-tight text-balance">
              Guiding <span className="text-brand-primary">Excellence, Ethics</span> and Vision in Eye Care.
            </h2>

            <div className="relative mt-8">
              <Quote size={54} className="text-brand-primary/15 absolute -top-4 -left-2" />

              <p className="text-brand-muted leading-relaxed text-lg md:text-xl italic pl-10 border-l-2 border-brand-primary/30 ml-2">
                {s.leader_message}
              </p>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-10 h-px bg-brand-primary" />

              <div className="text-sm text-brand-text font-semibold">
                {s.leader_name}, {s.leader_designation || "Chief Patron"} - GOS
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMITTEE PREVIEW */}
      <section className="section">
        <div className="container-wide">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4"><span className="eyebrow-line" />Organization</div>
              <h2 className="font-display text-[clamp(2rem,3.6vw,3.25rem)] font-extrabold text-brand-text leading-[1.08] tracking-tight">Office Bearers</h2>
              <p className="text-brand-muted mt-4 text-[17px] leading-relaxed">A dedicated team of senior ophthalmologists steering the Society's academic and professional initiatives.</p>
            </div>
            <Link to="/executive-committee" className="btn-outline !py-2.5 !px-5 text-sm">View All <ChevronRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {committee.map((c, i) => (
              <FadeUp key={c.id} delay={i * 0.06} className="group relative rounded-3xl overflow-hidden bg-white border border-brand-border/70 hover:border-brand-primary/30 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_50px_-20px_rgba(0,86,166,0.3)] hover:-translate-y-1 transition-all duration-500">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={mediaUrl(c.photo)} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-transparent to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-brand-secondary font-semibold">{c.designation}</div>
                  <div className="font-display text-lg font-bold mt-1">{c.name}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS + ANNOUNCEMENTS */}
      <section className="section-tight bg-gradient-to-br from-brand-primary-light/40 via-white to-brand-secondary-light/30">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="eyebrow mb-3"><span className="eyebrow-line" />Upcoming Events</div>
                <h3 className="font-display text-[clamp(1.5rem,2.6vw,2.25rem)] font-extrabold text-brand-text leading-tight tracking-tight">Mark Your Calendar</h3>
              </div>
              <Link to="/events" className="text-brand-primary font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">All Events <ChevronRight size={16} /></Link>
            </div>
            <div className="space-y-4">
              {events.map((e, i) => {
                const d = new Date(e.event_date);
                return (
                  <FadeUp key={e.id} delay={i * 0.05}>
                    <Link to={`/events/${e.slug}`} className="group flex gap-5 p-5 rounded-2xl bg-white border border-brand-border/70 shadow-sm hover:shadow-[0_20px_45px_-20px_rgba(0,86,166,0.3)] hover:border-brand-primary/30 hover:-translate-y-0.5 transition-all">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-primary to-brand-primary-hover text-white flex flex-col items-center justify-center shrink-0 shadow-md">
                        <div className="text-[10px] uppercase tracking-widest">{d.toLocaleString("en", { month: "short" })}</div>
                        <div className="font-display text-3xl font-extrabold leading-none">{d.getDate()}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-brand-text text-[15px] group-hover:text-brand-primary transition-colors truncate">{e.title}</div>
                        <div className="text-[13px] text-brand-muted mt-1.5 flex items-center gap-1.5"><MapPin size={12} /> {e.venue}</div>
                        <div className="mt-2.5 inline-flex items-center px-2.5 py-1 rounded-full bg-brand-accent/10 text-brand-accent text-[10px] font-semibold uppercase tracking-widest">{e.category}</div>
                      </div>
                    </Link>
                  </FadeUp>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="eyebrow mb-3"><span className="eyebrow-line" />Latest Announcements</div>
                <h3 className="font-display text-[clamp(1.5rem,2.6vw,2.25rem)] font-extrabold text-brand-text leading-tight tracking-tight">Stay Informed</h3>
              </div>
              <Link to="/announcements" className="text-brand-primary font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">All News <ChevronRight size={16} /></Link>
            </div>
            <div className="space-y-4">
              {ann.slice(0, 4).map((a, i) => (
                <FadeUp key={a.id} delay={i * 0.05}>
                  <Link to={`/announcements/${a.slug}`} className="group flex gap-5 p-4 rounded-2xl bg-white border border-brand-border/70 shadow-sm hover:shadow-[0_20px_45px_-20px_rgba(0,86,166,0.3)] hover:border-brand-primary/30 hover:-translate-y-0.5 transition-all">
                    {a.image && <img src={a.image} alt={a.title} className="w-24 h-24 rounded-xl object-cover shrink-0 group-hover:scale-105 transition-transform duration-500" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-primary font-semibold">{a.category}</div>
                      <div className="font-display font-bold text-brand-text text-[15px] mt-1.5 line-clamp-2 group-hover:text-brand-primary transition-colors">{a.title}</div>
                      <div className="text-[13px] text-brand-muted mt-1 line-clamp-1">{a.summary}</div>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY - horizontal scroll */}
      <section className="section">
        <div className="container-wide">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4"><span className="eyebrow-line" />Gallery</div>
              <h2 className="font-display text-[clamp(2rem,3.6vw,3.25rem)] font-extrabold text-brand-text leading-[1.08] tracking-tight">Moments From Our Journey</h2>
              <p className="text-brand-muted mt-4 text-[17px] leading-relaxed">Highlights from conferences, workshops, and community outreach programs. <span className="text-brand-muted/70">Drag or scroll to explore →</span></p>
            </div>
            <Link to="/gallery" className="btn-outline !py-2.5 !px-5 text-sm">Explore Full Gallery <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="pl-[max(1rem,calc((100vw-1440px)/2+2.5rem))] pr-4">
          <HorizontalGallery items={gallery} testidPrefix="home-gallery-item" />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative overflow-hidden bg-brand-dark text-white">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-primary/20 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 rounded-full bg-brand-secondary/15 blur-3xl" />
        <div className="section container-wide relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="eyebrow text-brand-secondary mb-4 justify-center"><span className="inline-block w-8 h-px bg-brand-secondary" />Testimonials<span className="inline-block w-8 h-px bg-brand-secondary" /></div>
            <h2 className="font-display text-[clamp(2rem,3.6vw,3.25rem)] font-extrabold leading-[1.08] tracking-tight text-balance">Voices from our community</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeUp key={t.id} delay={i * 0.08} className="relative bg-white/5 backdrop-glass rounded-2xl border border-white/10 p-8 hover:border-white/25 hover:-translate-y-1 transition-all">
                <Quote size={30} className="text-brand-secondary/60 absolute top-6 right-6" />
                <div className="flex text-brand-accent gap-0.5 mb-5">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} size={13} fill="currentColor" />)}</div>
                <p className="text-white/90 leading-relaxed text-[15px]">"{t.message}"</p>
                <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/10">
                  {t.photo && <img src={t.photo} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20" />}
                  <div>
                    <div className="font-display font-bold text-white">{t.name}</div>
                    <div className="text-xs text-white/60 uppercase tracking-widest mt-0.5">{t.designation}</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="section-tight bg-white border-b border-brand-border">
        <div className="container-wide">
          <div className="text-center mb-10">
            <div className="eyebrow justify-center mb-4"><span className="eyebrow-line" />Partner Institutions<span className="eyebrow-line" /></div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-text tracking-tight">Trusted by leading hospitals across NCR</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {PARTNERS.map((p) => (
              <div key={p} className="h-20 rounded-xl border border-brand-border/70 flex items-center justify-center px-4 hover:border-brand-primary/30 hover:bg-brand-primary-light/30 transition-all group">
                <div className="font-display font-bold text-brand-muted group-hover:text-brand-primary transition-colors text-center text-sm leading-tight">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-tight">
        <div className="container-wide">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-primary via-[#0068c9] to-brand-secondary p-10 md:p-16 text-white shadow-[0_40px_80px_-30px_rgba(0,86,166,0.5)]">
            <div className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, white 1.5px, transparent 1.5px)", backgroundSize: "28px 28px" }} />
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <div className="eyebrow text-white/80 mb-4"><span className="inline-block w-8 h-px bg-white/60" />Join the Society</div>
                <h2 className="font-display text-[clamp(1.75rem,3.2vw,2.75rem)] font-extrabold leading-tight tracking-tight">Ready to advance your career in ophthalmology?</h2>
                <p className="mt-4 text-white/85 text-[17px] leading-relaxed max-w-2xl">Join a network of leading eye care professionals. Access exclusive CMEs, mentorship, and academic resources.</p>
              </div>
              <div className="md:text-right">
                <Link to="/become-member" className="btn-hero-light" data-testid="footer-cta-btn">Apply for Membership <ArrowRight size={18} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
